import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../lib/api";
import { logError, toUserMessage } from "../lib/errors";
import { storage } from "../lib/storage";
import { useAccountState } from "../hooks/useAccountState";
import { UI_TEXT_TONE } from "../lib/uiTone";

export default function SupportInquiryPage() {
  const navigate = useNavigate();
  const closePopup = () => navigate(-1);
  const { accountState, loading: accountLoading } = useAccountState();
  const isAuthenticated = accountState?.authStatus === "authenticated";
  const accountUserId = storage.getAuth().userId ?? null;

  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [contact, setContact] = useState("");
  const [message, setMessage] = useState("");
  const [nickname, setNickname] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    setError(null);
    setSubmitting(true);
    try {
      await api.submitSupportInquiry({
        name,
        company: company.trim() === "" ? undefined : company,
        contact,
        message,
        userId: isAuthenticated ? accountUserId ?? undefined : undefined,
        nickname: isAuthenticated ? undefined : (nickname.trim() === "" ? undefined : nickname),
      });
      setSubmitted(true);
    } catch (cause) {
      logError(cause);
      setError(toUserMessage(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] antialiased sm:px-6 sm:py-10">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-xl items-center justify-center">
        <div className="relative w-full max-w-full rounded-[2.4rem] border border-white/80 bg-white/90 p-4 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <button
            type="button"
            onClick={closePopup}
            aria-label="閉じる"
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#EADBC8] bg-[#FFF8F4] text-[#7C5A4E] transition hover:bg-white"
          >
            ×
          </button>

          <div className="mb-5 pr-12">
            <h1 className={`text-2xl font-black tracking-tight ${UI_TEXT_TONE.title}`}>アカウントに関するお問い合わせ</h1>
            <p className={`mt-2 text-sm leading-6 ${UI_TEXT_TONE.body}`}>
              ログインできない、以前のデータが見えない等、アカウントのお困りごとはこちらからご連絡ください。担当者より折り返しご連絡いたします。
            </p>
          </div>

          {submitted ? (
            <p role="status" className="rounded-[1rem] border border-[#EADBC8] bg-[#FDF8F5] px-4 py-4 text-sm font-bold text-[#5D4D46]">
              お問い合わせを受け付けました。担当者より折り返しご連絡いたします。
            </p>
          ) : (
          <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
            <div>
              <label htmlFor="support-inquiry-name" className="mb-1 block text-xs font-black text-[#A67C52]">
                お名前
              </label>
              <input
                id="support-inquiry-name"
                type="text"
                required
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="w-full rounded-[0.9rem] border border-[#EADBC8] px-4 py-2 text-sm text-[#4A3E39]"
              />
            </div>

            <div>
              <label htmlFor="support-inquiry-company" className="mb-1 block text-xs font-black text-[#A67C52]">
                会社名(任意)
              </label>
              <input
                id="support-inquiry-company"
                type="text"
                value={company}
                onChange={(event) => setCompany(event.target.value)}
                className="w-full rounded-[0.9rem] border border-[#EADBC8] px-4 py-2 text-sm text-[#4A3E39]"
              />
            </div>

            <div>
              <label htmlFor="support-inquiry-contact" className="mb-1 block text-xs font-black text-[#A67C52]">
                折り返し先(電話番号またはメールアドレス)
              </label>
              <input
                id="support-inquiry-contact"
                type="text"
                required
                value={contact}
                onChange={(event) => setContact(event.target.value)}
                className="w-full rounded-[0.9rem] border border-[#EADBC8] px-4 py-2 text-sm text-[#4A3E39]"
              />
            </div>

            {!accountLoading && isAuthenticated ? (
              <div className="rounded-[1rem] border border-[#EADBC8] bg-[#FDF8F5] px-4 py-3">
                <p className="text-[10px] font-black tracking-[0.08em] text-[#A67C52]">ユーザーID</p>
                <p className="mt-1 break-all font-mono text-[11px] font-bold text-[#5D4D46]">
                  {accountUserId ?? "-"}
                </p>
                <p className="mt-1 text-[11px] text-[#9C867D]">ログイン中のため自動で送信されます。</p>
              </div>
            ) : null}

            {!accountLoading && !isAuthenticated ? (
              <div>
                <label htmlFor="support-inquiry-nickname" className="mb-1 block text-xs font-black text-[#A67C52]">
                  WizMateでの呼び名
                </label>
                <input
                  id="support-inquiry-nickname"
                  type="text"
                  value={nickname}
                  onChange={(event) => setNickname(event.target.value)}
                  className="w-full rounded-[0.9rem] border border-[#EADBC8] px-4 py-2 text-sm text-[#4A3E39]"
                />
                <p className="mt-1 text-[11px] text-[#9C867D]">プロフィール設定で登録した呼び名です。わかる範囲でご入力ください。</p>
              </div>
            ) : null}

            <div>
              <label htmlFor="support-inquiry-message" className="mb-1 block text-xs font-black text-[#A67C52]">
                お困りの内容
              </label>
              <textarea
                id="support-inquiry-message"
                required
                rows={4}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                className="w-full rounded-[0.9rem] border border-[#EADBC8] px-4 py-2 text-sm text-[#4A3E39]"
              />
            </div>

            {error ? (
              <p role="alert" className="text-xs font-bold text-[#B4533A]">
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-full border border-[#A67C52] bg-[#A67C52] px-4 py-2 text-sm font-black text-white transition hover:bg-[#8f6a45] disabled:opacity-50"
            >
              {submitting ? "送信中..." : "送信する"}
            </button>
          </form>
          )}
        </div>
      </div>
    </main>
  );
}
